/**
 * API Client
 * HttpOnly cookie + CSRF token authentication
 * No JWT in localStorage
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const envUrl = (import.meta as any).env?.VITE_API_URL;
const API_BASE_URL = envUrl === '' ? '' : (envUrl || 'http://localhost:3000/v1');

interface ApiError {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: Array<{ field: string; message: string }>;
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) return decodeURIComponent(match[2]);
}

class ApiClient {
  private client: AxiosInstance;
  private tenantId: string | null = null;
  private csrfToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // CSRF token for state-changing requests
        if (!['GET', 'HEAD', 'OPTIONS'].includes(config.method?.toUpperCase() || '')) {
          const token = this.csrfToken || getCookie('csrf_token');
          if (token) {
            config.headers['X-CSRF-Token'] = token;
          }
        }
        if (this.tenantId) {
          config.headers['X-Tenant-ID'] = this.tenantId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        if (error.response?.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true;
          try {
            await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
            return this.client.request(originalConfig);
          } catch {
            this.clearAuth();
            if (!window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setTenantId(tenantId: string) {
    this.tenantId = tenantId;
    localStorage.setItem('dpp_tenant_id', tenantId);
  }

  loadFromStorage() {
    this.tenantId = localStorage.getItem('dpp_tenant_id');
  }

  clearAuth() {
    this.tenantId = null;
    this.csrfToken = null;
    localStorage.removeItem('dpp_tenant_id');
  }

  get isAuthenticated() {
    // We can't read httpOnly cookies from JS; backend will tell us via /auth/me
    return true;
  }

  // Auth
  async login(email: string, password: string, tenantSlug: string) {
    const response = await this.client.post('/auth/login', { email, password, tenant_slug: tenantSlug });
    const { csrf_token, user } = response.data;
    if (csrf_token) {
      this.csrfToken = csrf_token;
      document.cookie = `csrf_token=${encodeURIComponent(csrf_token)}; path=/; SameSite=Strict`;
    }
    this.setTenantId(user.tenant.id);
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.client.get('/auth/me');
    return response.data.user;
  }

  async logout() {
    await this.client.post('/auth/logout');
    this.clearAuth();
  }

  // Sector Templates
  async getSectorTemplates() {
    const response = await this.client.get('/sector-templates');
    return response.data;
  }

  // Passports
  async getPassports(params?: { status?: string; sector_id?: string; page?: number; limit?: number }) {
    const response = await this.client.get('/passports', { params });
    return response.data;
  }

  async getPassport(id: string) {
    const response = await this.client.get(`/passports/${id}`);
    return response.data;
  }

  async createPassport(data: Record<string, any>) {
    const response = await this.client.post('/passports', data);
    return response.data;
  }

  async updatePassport(id: string, data: Record<string, any>) {
    const response = await this.client.put(`/passports/${id}`, data);
    return response.data;
  }

  async publishPassport(id: string) {
    const response = await this.client.post(`/passports/${id}/publish`);
    return response.data;
  }

  async archivePassport(id: string) {
    const response = await this.client.delete(`/passports/${id}`);
    return response.data;
  }

  async getPassportQr(id: string, format: 'png' | 'svg' = 'png') {
    const response = await this.client.get(`/passports/${id}/qr-code`, {
      params: { format },
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  }

  // Compliance
  async getComplianceStats() {
    const response = await this.client.get('/compliance/summary');
    return response.data as {
      pass: number;
      fail: number;
      warning: number;
      pending: number;
      published: number;
      total: number;
    };
  }

  async validateCompliance(passportId: string, passportData?: Record<string, any>) {
    const response = await this.client.post('/compliance/validate', {
      passport_id: passportId,
      passport_data: passportData,
    });
    return response.data;
  }

  // Audit Log
  async getAuditLog(params?: { table_name?: string; record_id?: string; page?: number; limit?: number }) {
    const response = await this.client.get('/audit-log', { params });
    return response.data;
  }

  // Access Requests
  async submitAccessRequest(data: {
    email: string;
    organization_name: string;
    organization_type: string;
    access_tier: 'authorized' | 'regulatory';
    request_reason: string;
    documents?: Array<{ name: string; url: string }>;
  }) {
    const response = await this.client.post('/access-requests', data);
    return response.data;
  }

  async getAccessRequestStatus(id: string) {
    const response = await this.client.get(`/access-requests/status/${id}`);
    return response.data;
  }

  async listAccessRequests() {
    const response = await this.client.get('/access-requests');
    return response.data;
  }

  async reviewAccessRequest(id: string, data: { status: 'approved' | 'rejected'; reviewer_notes?: string }) {
    const response = await this.client.post(`/access-requests/${id}/review`, data);
    return response.data;
  }

  // ── CBAM ──────────────────────────────────────────────────────────────────
  async getCbamSummary() {
    const response = await this.client.get('/cbam/summary');
    return response.data;
  }

  async getCbamBatches(quarter?: string) {
    const response = await this.client.get('/cbam/batches', { params: quarter ? { quarter } : undefined });
    return response.data;
  }

  async createCbamBatch(data: Record<string, any>) {
    const response = await this.client.post('/cbam/batches', data);
    return response.data;
  }

  async calculateCbamEmissions(batchId: string, supplierData?: Record<string, any>) {
    const response = await this.client.post(`/cbam/batches/${batchId}/calculate`, { supplierData });
    return response.data;
  }

  async importCbamBatches(rows: Record<string, any>[]) {
    const response = await this.client.post('/cbam/import-batch', { rows });
    return response.data;
  }

  async getCbamQuarterlyReports() {
    const response = await this.client.get('/cbam/quarterly');
    return response.data;
  }

  async generateCbamReport(quarter: string) {
    const response = await this.client.post(`/cbam/quarterly/${quarter}/generate`, {});
    return response.data;
  }

  async getCbamReport(quarter: string, format?: 'json' | 'csv') {
    if (format === 'csv') {
      const response = await this.client.get(`/cbam/quarterly/${quarter}`, {
        params: { format: 'csv' },
        responseType: 'blob',
      });
      return response.data;
    }
    const response = await this.client.get(`/cbam/quarterly/${quarter}`);
    return response.data;
  }

  // ── ESPR Repairability ────────────────────────────────────────────────────
  async getRepairabilityScore(dppId: string) {
    const response = await this.client.get(`/sustainability/${dppId}/repairability`);
    return response.data;
  }

  async getRepairabilityHistory(dppId: string) {
    const response = await this.client.get(`/sustainability/${dppId}/repairability/history`);
    return response.data;
  }

  async calculateRepairability(dppId: string, input: Record<string, any>) {
    const response = await this.client.post(`/sustainability/${dppId}/repairability/calculate`, input);
    return response.data;
  }

  async previewRepairability(input: Record<string, any>) {
    const response = await this.client.post('/sustainability/calculate/preview', input);
    return response.data;
  }

  async listRepairabilityScores() {
    const response = await this.client.get('/sustainability/scores');
    return response.data;
  }

  // ── PPWR Packaging ────────────────────────────────────────────────────────
  async getPackagingSummary() {
    const response = await this.client.get('/packaging/summary');
    return response.data;
  }

  async getPackagingUnits(dppId?: string) {
    const url = dppId ? `/packaging/${dppId}` : '/packaging';
    const response = await this.client.get(url);
    return response.data;
  }

  async createPackagingUnit(dppId: string, data: Record<string, any>) {
    const response = await this.client.post(`/packaging/${dppId}`, data);
    return response.data;
  }

  async updatePackagingUnit(dppId: string, unitId: string, data: Record<string, any>) {
    const response = await this.client.put(`/packaging/${dppId}/${unitId}`, data);
    return response.data;
  }

  async deletePackagingUnit(dppId: string, unitId: string) {
    const response = await this.client.delete(`/packaging/${dppId}/${unitId}`);
    return response.data;
  }

  async getEprReports() {
    const response = await this.client.get('/packaging/epr');
    return response.data;
  }

  async generateEprReport(year: number) {
    const response = await this.client.post(`/packaging/epr/${year}/generate`, {});
    return response.data;
  }

  async downloadEprReport(year: number, format: 'json' | 'csv' = 'csv') {
    if (format === 'csv') {
      const response = await this.client.get(`/packaging/epr/${year}`, {
        params: { format: 'csv' }, responseType: 'blob',
      });
      return response.data;
    }
    const response = await this.client.get(`/packaging/epr/${year}`);
    return response.data;
  }
}

export const api = new ApiClient();
